import { useEffect, useMemo, useState } from 'react';
import TickerSymbol from '@/components/TickerSymbol';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency, getRelativeTime, formatPersonName } from '@/lib/utils';
import PValueBadge from '@/components/PValueBadge';
import Sparkline from '@/components/Sparkline';
import { useSparklineSeries } from '@/hooks/useSparklineSeries';

/**
 * LiveSmartSignalSidebar — recency-first tiered fallback selection.
 *
 * Selection rules (see mem://features/live-smart-signal-sidebar):
 *   Tier 1: A/A+ graded insider buys, last 7 days
 *   Tier 2: High-conviction cluster signals (cluster_strength_7_plus / cluster_3_plus
 *           from signal_outcomes, OR insider_filings_aggregated.is_high_conviction), last 7 days
 *   Tier 3: Any buy ≥ $250k, last 7 days
 *   Tier 4 (fallback if tiers 1–3 yield <5 rows): most recent A/A+ buys, last 30 days
 *
 * Dedupe by (ticker, filer_name). Sort by tier asc, then filing_date desc.
 *
 * Live badge ("LIVE FEED" + cyan dot):
 *   - newest item ≤ 48h: pulse animation
 *   - newest item 48h–7d: static dot, "LIVE FEED" label
 *   - newest item > 7d (tier-4 only): static dot, "RECENT ACTIVITY" label
 */

type Tier = 1 | 2 | 3 | 4;

interface SidebarRow {
  id: string;
  ticker: string;
  filer_name: string;
  total_value: number;
  filing_date: string; // ISO date
  tier: Tier;
  p_value?: number | null;
  mean_excess_return?: number | null;
  settled_count?: number | null;
}

const ROW_HEIGHT = 56;
const VISIBLE_ROWS = 5;

const formatRelative = (dateIso: string): string => {
  // Returns "today", "yesterday", or "Xd ago".
  const d = new Date(dateIso + 'T00:00:00Z');
  const today = new Date();
  const utcToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const utcD = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const diff = Math.round((utcToday - utcD) / 86400000);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  return `${diff}d ago`;
};

const ageDays = (dateIso: string): number => {
  const d = new Date(dateIso + 'T00:00:00Z').getTime();
  return (Date.now() - d) / 86400000;
};

const LiveSmartSignalSidebar = () => {
  const [rows, setRows] = useState<SidebarRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Performance map for statistically-significant insiders (p < 0.05, beats SPY).
        const { data: graded } = await supabase
          .from('insider_performance')
          .select('filer_name, p_value, mean_excess_return, settled_count');
        type PStats = { p: number | null; mean: number | null; n: number | null };
        const statsMap = new Map<string, PStats>();
        for (const g of (graded || []) as any[]) {
          statsMap.set(g.filer_name, {
            p: g.p_value != null ? Number(g.p_value) : null,
            mean: g.mean_excess_return != null ? Number(g.mean_excess_return) : null,
            n: g.settled_count != null ? Number(g.settled_count) : null,
          });
        }
        const isSignificant = (s?: PStats) =>
          !!s && s.p != null && s.p < 0.05 && (s.mean ?? 0) > 0 && (s.n ?? 0) >= 3;
        const significantNames = new Set<string>(
          Array.from(statsMap.entries())
            .filter(([, s]) => isSignificant(s))
            .map(([name]) => name)
        );

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const sevenISO = sevenDaysAgo.toISOString().split('T')[0];

        // Trades from last 7 days (covers tiers 1–3 candidates)
        const { data: recentTrades } = await supabase
          .from('insider_trades')
          .select('id, ticker, filer_name, total_value, filing_date')
          .eq('transaction_type', 'buy')
          .eq('price_anomaly_flagged', false)
          .gte('filing_date', sevenISO)
          .order('filing_date', { ascending: false })
          .order('total_value', { ascending: false, nullsFirst: false })
          .limit(150);

        // High-conviction cluster signals from last 7 days
        const { data: clusterSignals } = await supabase
          .from('signal_outcomes')
          .select('trade_id, flag_reason, flagged_at')
          .in('flag_reason', ['cluster_strength_7_plus', 'cluster_3_plus'])
          .gte('flagged_at', sevenDaysAgo.toISOString());
        const clusterTradeIds = new Set<string>((clusterSignals || []).map((c: any) => c.trade_id));

        // High-conviction aggregated filings (per is_high_conviction flag)
        const { data: hcAgg } = await supabase
          .from('insider_filings_aggregated')
          .select('ticker, filer_name')
          .gte('filing_date', sevenISO)
          .eq('is_high_conviction', true);
        const hcKeys = new Set<string>(
          (hcAgg || []).map((r: any) => `${r.ticker}::${r.filer_name}`)
        );

        // Build candidate list with tier assignment
        const candidates: SidebarRow[] = [];
        for (const t of (recentTrades || []) as any[]) {
          const value = Number(t.total_value) || 0;
          const stats = statsMap.get(t.filer_name);
          const significant = significantNames.has(t.filer_name);
          let tier: Tier | null = null;
          if (significant) tier = 1;
          else if (clusterTradeIds.has(t.id) || hcKeys.has(`${t.ticker}::${t.filer_name}`)) tier = 2;
          else if (value >= 250_000) tier = 3;
          if (!tier) continue;
          candidates.push({
            id: t.id,
            ticker: t.ticker,
            filer_name: t.filer_name,
            total_value: value,
            filing_date: t.filing_date,
            tier,
            p_value: stats?.p ?? null,
            mean_excess_return: stats?.mean ?? null,
            settled_count: stats?.n ?? null,
          });
        }

        // Dedupe by (ticker, filer_name) keeping best tier (tier asc, then newest, then largest)
        const dedupSorted = [...candidates].sort((a, b) => {
          if (a.tier !== b.tier) return a.tier - b.tier;
          if (a.filing_date !== b.filing_date) return b.filing_date.localeCompare(a.filing_date);
          return (b.total_value || 0) - (a.total_value || 0);
        });
        const seen = new Set<string>();
        const dedupedAll: SidebarRow[] = [];
        for (const c of dedupSorted) {
          const key = `${c.ticker}::${c.filer_name}`;
          if (seen.has(key)) continue;
          seen.add(key);
          dedupedAll.push(c);
        }
        // Recency-first display sort: filing_date desc, then tier asc, then value desc
        dedupedAll.sort((a, b) => {
          if (a.filing_date !== b.filing_date) return b.filing_date.localeCompare(a.filing_date);
          if (a.tier !== b.tier) return a.tier - b.tier;
          return (b.total_value || 0) - (a.total_value || 0);
        });
        const deduped: SidebarRow[] = dedupedAll.slice(0, VISIBLE_ROWS);

        // Tier 4 fallback: most recent significant insiders, last 30 days
        if (deduped.length < VISIBLE_ROWS && significantNames.size > 0) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const thirtyISO = thirtyDaysAgo.toISOString().split('T')[0];
          const { data: fallback } = await supabase
            .from('insider_trades')
            .select('id, ticker, filer_name, total_value, filing_date')
            .eq('transaction_type', 'buy')
            .eq('price_anomaly_flagged', false)
            .gte('filing_date', thirtyISO)
            .lt('filing_date', sevenISO)
            .in('filer_name', Array.from(significantNames))
            .order('filing_date', { ascending: false })
            .limit(30);
          for (const t of (fallback || []) as any[]) {
            const key = `${t.ticker}::${t.filer_name}`;
            if (seen.has(key)) continue;
            seen.add(key);
            const s = statsMap.get(t.filer_name);
            deduped.push({
              id: t.id,
              ticker: t.ticker,
              filer_name: t.filer_name,
              total_value: Number(t.total_value) || 0,
              filing_date: t.filing_date,
              tier: 4,
              p_value: s?.p ?? null,
              mean_excess_return: s?.mean ?? null,
              settled_count: s?.n ?? null,
            });
            if (deduped.length >= VISIBLE_ROWS) break;
          }
        }

        setRows(deduped);
      } catch (err) {
        console.error('LiveSmartSignalSidebar load failed', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Determine badge state from newest item
  const { badgeLabel, pulse } = useMemo(() => {
    if (rows.length === 0) return { badgeLabel: 'LIVE BUYS', pulse: false };
    const newestAge = Math.min(...rows.map((r) => ageDays(r.filing_date)));
    if (newestAge <= 2) return { badgeLabel: 'LIVE BUYS', pulse: true };
    if (newestAge <= 7) return { badgeLabel: 'LIVE BUYS', pulse: false };
    return { badgeLabel: 'RECENT BUYS', pulse: false };
  }, [rows]);

  const sparkSeries = useSparklineSeries(rows.map((r) => r.ticker), 30);

  const SkeletonRow = () => (
    <div className="flex items-center justify-between px-4 py-3" style={{ height: ROW_HEIGHT }}>
      <div className="space-y-1.5">
        <div className="h-3 w-20 animate-pulse rounded bg-muted/50" />
        <div className="h-2.5 w-32 animate-pulse rounded bg-muted/30" />
      </div>
      <div className="h-3 w-16 animate-pulse rounded bg-muted/40" />
    </div>
  );

  return (
    <aside className="rounded-md border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-live-dot" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground">
            {badgeLabel}
          </span>
        </div>
        <Link
          to="/significant-trades"
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
        >
          All <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div
        className="relative overflow-hidden"
        style={{ height: loading || rows.length === 0 ? 'auto' : `${VISIBLE_ROWS * ROW_HEIGHT}px` }}
      >
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-6 text-center">
            <Sparkles className="h-5 w-5 text-muted-foreground" />
            <p className="mt-2 text-xs text-muted-foreground">
              No qualifying insider activity in the last 7 days.
            </p>
          </div>
        ) : (
          <div>
            {rows.map((s) => (
              <Link
                key={s.id}
                to={`/stock/${s.ticker}`}
                className="flex items-center justify-between border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-muted"
                style={{ height: ROW_HEIGHT }}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <TierBadge row={s} />
                  <div className="min-w-0">
                    <TickerSymbol ticker={s.ticker} asLink={false} size="sm" />
                    <div className="truncate text-[11px] text-muted-foreground">{formatPersonName(s.filer_name)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {(() => {
                    const series = sparkSeries[s.ticker.toUpperCase()];
                    return series && series.length > 1 ? (
                      <Sparkline data={series} width={56} height={18} />
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/60">N/A</span>
                    );
                  })()}
                  <div className="text-right">
                    <div className="font-mono text-xs font-medium text-positive tabular-nums">
                      {s.total_value > 0 ? formatCurrency(s.total_value) : (<span className="text-muted-foreground/60">N/A</span>)}
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">{formatRelative(s.filing_date)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border px-4 py-2.5">
        <p className="text-[10px] leading-relaxed text-muted-foreground">
          Top-ranked insiders, high-conviction clusters, and notable buys.{' '}
          <Link to="/significant-trades" className="text-primary hover:underline">
            See full feed →
          </Link>
        </p>
      </div>
    </aside>
  );
};

const TierBadge = ({ row }: { row: SidebarRow }) => {
  if (row.tier === 1) {
    return (
      <PValueBadge
        pValue={row.p_value ?? null}
        meanExcessReturn={row.mean_excess_return ?? null}
        settledCount={row.settled_count ?? null}
        size="sm"
      />
    );
  }
  if (row.tier === 2) {
    return (
      <span className="inline-flex h-5 items-center rounded-sm bg-muted px-1.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
        Cluster
      </span>
    );
  }
  return (
    <span className="inline-flex h-5 items-center rounded-sm bg-muted px-1.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
      Large
    </span>
  );
};

export default LiveSmartSignalSidebar;
